import React, {
  useState,
  Children,
  useEffect,
  useRef,
  useMemo,
  memo,
} from 'react';

import getPossibleFocusableElements from 'Utils/functions/getPossibleFocusableElements';
import useIsKeyboardFocus from 'Utils/hooks/useIsKeyboardFocus';
import CarouselNavigation from 'Custom/Carousel/CarouselNavigation';
import CarouselControls from 'Custom/Carousel/CarouselControls';

import styles from 'Nori/MultipleCarousel/index.module.scss';

const MIN_SWIPED_DISTANCE = 150;
const MIN_TOUCHED_DISTANCE = 50;
const DELAY = 500;
const EVENT_TYPES = {
  swipe: 'swipe',
  click: 'click',
  navigation: 'navigation',
};

const MultipleCarousel = ({
  children,
  className = '',
  navigationItems = [],
  classNameItem = styles.carouselItem,
  initialActiveIndex = 0,
  showDots = false,
  showControls = true,
  centerMode = false,
}) => {
  const listRef = useRef(null);
  const length = Children.count(children);
  const isKeyboardFocus = useIsKeyboardFocus(listRef);

  const [eventType, setEventType] = useState('');
  const [show, setShow] = useState(0);
  const [transitionEnabled, setTransitionEnabled] = useState(false);
  const [activeIndex, setActiveIndex] = useState(length);
  const [itemWidth, setItemWidth] = useState(0);
  const [swipeMovement, setSwipeMovement] = useState(0);
  const [moving, setMoving] = useState(false);
  const [canMove, setCanMove] = useState(true);

  const [isDown, setIsDown] = useState(false);
  const [swipeStartPosition, setSwipeStartPosition] = useState(0);

  const currentPosition = parseFloat(itemWidth) * activeIndex;
  const halfShow = Math.floor(show / 2);

  const activeItem = useMemo(() => {
    const limit = Math.min(show, length);
    const i = (activeIndex - limit) % length;

    return i >= 0 ? Math.abs(i) : Math.abs(length + i);
  }, [show, activeIndex, length]);

  const accessibilityHandler = (container) => {
    if (!container) {
      return;
    }

    const track = container.parentElement;
    const trackRect = track.getBoundingClientRect();

    return (item) => {
      const itemRect = item.getBoundingClientRect();
      const isVisible =
        itemRect.left >= trackRect.left && !item.dataset.nextItem;
      const focusableElements = getPossibleFocusableElements(item);
      const newTabIndex = isVisible ? '0' : '-1';

      focusableElements.forEach((el) => {
        el.setAttribute('tabindex', newTabIndex);
      });
    };
  };

  useEffect(() => {
    const carouselItems = Array.from(listRef.current.children);
    carouselItems.forEach((item) => {
      item.classList.remove('active');
    });
    listRef.current.children[activeIndex].classList.add('active');

    const handleAccessibilityByItem = accessibilityHandler(listRef.current);
    const accessibilityTimeout = setTimeout(() => {
      carouselItems.forEach((item) => {
        handleAccessibilityByItem(item);
      });
    }, DELAY);

    return () => {
      clearTimeout(accessibilityTimeout);
    };
  }, [activeIndex]);

  useEffect(() => {
    // Resize ---
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.contentRect) {
          const { current: listElement } = listRef;

          if (!listElement) {
            return;
          }

          const gap = parseInt(getComputedStyle(listElement).columnGap);
          const width =
            listElement.querySelector('li:not(.active)').clientWidth + gap;

          const itemsPerPage = Math.min(
            length,
            Math.ceil(entry.contentRect.width / width)
          );
          const activeElement = listElement.querySelector('.active');

          const activePosition = listElement.classList.contains('initialized')
            ? Array.from(activeElement.parentNode.children).indexOf(
                activeElement
              )
            : initialActiveIndex + itemsPerPage;

          setItemWidth(width);
          setShow((prevState) => Math.max(prevState, itemsPerPage));
          setActiveIndex(activePosition);

          listElement.classList.add('initialized');
        }
      }
    });
    resizeObserver.observe(listRef.current);

    return () => {
      if (listRef.current) {
        resizeObserver.unobserve(listRef.current);
      }
    };
  }, []);

  const next = (ev) => {
    if (!canMove) {
      return;
    }

    setEventType(ev?.type);
    setCanMove(false);
    setTransitionEnabled(true);
    if (activeIndex < length + show) {
      setActiveIndex((prevState) => prevState + 1);
    }

    setTimeout(() => {
      setCanMove(true);
    }, DELAY);
  };

  const prev = (ev) => {
    if (!canMove) {
      return;
    }

    setEventType(ev?.type);
    setCanMove(false);
    setTransitionEnabled(true);

    if (activeIndex > 0) {
      setActiveIndex((prevState) => prevState - 1);
    }

    setTimeout(() => {
      setCanMove(true);
    }, DELAY);
  };

  const nextTimeout = () => {
    setMoving(true);

    if (!moving) {
      next({ type: EVENT_TYPES.swipe });
    }
  };

  const prevTimeout = () => {
    setMoving(true);

    if (!moving) {
      prev({ type: EVENT_TYPES.swipe });
    }
  };

  const handleTransitionEnd = () => {
    const leftReset = centerMode ? halfShow : 0;
    const leftResetIndex = centerMode ? length + halfShow : length;

    if (activeIndex === leftReset) {
      setTransitionEnabled(false);
      setActiveIndex(leftResetIndex);
    } else if (activeIndex === length + show) {
      setTransitionEnabled(false);
      setActiveIndex(show);
    }
  };

  const goTo = (target) => {
    setTransitionEnabled(true);
    setActiveIndex(target + Math.min(show, length));
    setEventType(EVENT_TYPES.navigation);
  };

  useEffect(() => {
    const handleFocusIn = (event) => {
      if (isKeyboardFocus) {
        const item = event.target.closest(`.${classNameItem}`);
        const index = Number(item.dataset.index);
        goTo(index);
      }
    };

    if (listRef.current) {
      listRef.current.addEventListener('focusin', handleFocusIn);
    }

    return () => {
      if (listRef.current) {
        listRef.current.removeEventListener('focusin', handleFocusIn);
      }
    };
  }, [show, length, isKeyboardFocus]);

  const clonePrev = useMemo(
    () =>
      Children.toArray(children)
        .slice(-show)
        .map((child, index) => (
          <li
            key={index}
            data-index={index}
            data-clone={`prev-${index}`}
            className={classNameItem}
          >
            {child}
          </li>
        )),
    [show]
  );

  const cloneNext = useMemo(
    () =>
      Children.toArray(children)
        .slice(0, show)
        .map((child, index) => (
          <li
            key={index}
            data-index={index}
            data-next-item="true"
            data-clone={`next-${index}`}
            className={classNameItem}
          >
            {child}
          </li>
        )),
    [show]
  );

  const items = useMemo(
    () =>
      Children.map(children, (child, index) => (
        <li key={index} className={classNameItem} data-index={index}>
          {child}
        </li>
      )),
    []
  );

  const handleMouseDown = (e) => {
    if (e.target.nodeName === 'P') {
      return;
    }

    setIsDown(true);
    setSwipeStartPosition(e.clientX);
    listRef.current.parentElement.classList.add('swiping');
  };

  const stopSwipe = () => {
    setIsDown(false);
    setSwipeMovement(0);
    listRef.current.parentElement.classList.remove('swiping');
  };

  const handleMouseMove = (e) => {
    if (!isDown || !swipeStartPosition) {
      return;
    }

    const diff = swipeStartPosition - e.clientX;
    setSwipeMovement(diff * 0.8);
    if (diff > MIN_SWIPED_DISTANCE) {
      stopSwipe();
      next({ type: EVENT_TYPES.swipe });
    }

    if (diff < -MIN_SWIPED_DISTANCE) {
      stopSwipe();
      prev({ type: EVENT_TYPES.swipe });
    }

    e.preventDefault();
  };

  const handleTouchStart = (e) => {
    const touchDown = e.touches[0].clientX;
    setSwipeStartPosition(touchDown);
  };

  const handleTouchEnd = (e) => {
    setSwipeMovement(0);
    setMoving(false);
  };

  const handleTouchMove = (e) => {
    if (!swipeStartPosition && moving) {
      return;
    }

    const diff = swipeStartPosition - e.touches[0].clientX;
    setSwipeMovement(diff * 0.8);

    if (diff > MIN_TOUCHED_DISTANCE) {
      nextTimeout();
    }

    if (diff < -MIN_TOUCHED_DISTANCE) {
      prevTimeout();
    }
  };

  useEffect(() => {
    if (eventType === EVENT_TYPES.swipe && window.dataLayer) {
      const EVENT_SWIPE = {
        event: 'data-design-mobile',
        'data-design-type': 'swipeAction',
        eventCategory: 'content_tabs_vd',
        eventAction: 'Swipe-Number',
        eventLabel: Number(activeItem) + 1,
        'data-design-nori_id': 'Carousel',
        'data-design-module_name': 'GET TO KNOW NEXPLANON',
        'data-design-module_no': '01',
      };

      window.dataLayer.push(EVENT_SWIPE);
    }
  }, [activeItem, eventType]);

  return (
    <>
      {showControls && <CarouselControls onPrev={prev} onNext={next} />}
      <div
        className={`${styles.carouselComponent} ${
          centerMode ? styles.centerMode : ''
        } ${className}`}
      >
        <div
          className={styles.carouselTrack}
          onMouseDown={handleMouseDown}
          onMouseUp={stopSwipe}
          onMouseLeave={stopSwipe}
          onMouseMove={handleMouseMove}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <ul
            onTransitionEnd={handleTransitionEnd}
            className={styles.carouselList}
            ref={listRef}
            style={{
              transform: `translateX(-${currentPosition + swipeMovement}px)`,
              transition: !transitionEnabled ? 'none' : undefined,
            }}
          >
            {clonePrev}
            {items}
            {cloneNext}
          </ul>
        </div>
      </div>
      {showDots && (
        <CarouselNavigation
          items={navigationItems}
          activeItem={activeItem}
          onMove={goTo}
        />
      )}
    </>
  );
};

export default memo(MultipleCarousel);
